import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';

import { STRUCTURE_TAB_ID_PREFIX, STRUCTURE_TABS, StructureTabKey } from './structureTabs';

export interface StructureTabStripProps {
  activeTab: StructureTabKey;
  onTabChange: (tab: StructureTabKey) => void;
  /** Tabs to render, in display order. An empty list renders nothing. */
  visibleTabs: StructureTabKey[];
  /**
   * Tabs holding a field that failed validation. Marked so a Save blocked by a
   * required field on a tab the user cannot see does not look like a dead button.
   */
  invalidTabs?: StructureTabKey[];
}

const tabSx = (active: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 2,
  py: 1,
  fontSize: '13px',
  fontWeight: active ? 600 : 500,
  // `accentDark`, not `accent`: white-background teal text at 13px needs the
  // AA 4.5:1 bar and `accent` is 3.74:1. The underline below keeps `accent` —
  // it is a border, judged against the 3:1 non-text threshold.
  color: active ? 'v4.accentDark' : 'text.secondary',
  // `border` must be reset BEFORE the bottom-border rules: emotion emits these
  // in key order, so a trailing `border: none` would wipe the active underline.
  border: 'none',
  borderBottom: active ? '2px solid' : '2px solid transparent',
  borderBottomColor: active ? 'v4.accent' : 'transparent',
  cursor: 'pointer',
  background: 'none',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap' as const,
  mb: '-2px',
});

/**
 * Tab strip for the structure detail page, matching the Job and Part Type
 * strips. Each tab is a real `<button>` so keyboard activation works without
 * extra handlers, and is wired to its panel via aria-controls.
 */
export const StructureTabStrip: React.FC<StructureTabStripProps> = ({
  activeTab,
  onTabChange,
  visibleTabs,
  invalidTabs = [],
}) => {
  if (visibleTabs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mx: 2.5, '@media print': { display: 'none' } }}>
      <Box
        role="tablist"
        aria-label="Structure detail tabs"
        sx={{
          display: 'flex',
          gap: 0,
          borderBottom: '2px solid',
          borderColor: 'v4.borderDark',
          // The strip scrolls sideways when the tab set is too wide. overflowY
          // must be pinned: a scrolling overflow-x forces overflow-y to compute
          // to auto, and each tab's -2px bottom margin (which overlaps the
          // strip's border) then overflows the box and shows a stray vertical
          // scrollbar.
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        {STRUCTURE_TABS.filter(({ key }) => visibleTabs.includes(key)).map(({ key, label }) => {
          const active = activeTab === key;
          const invalid = invalidTabs.includes(key);
          return (
            <Box
              key={key}
              component="button"
              type="button"
              role="tab"
              id={`${STRUCTURE_TAB_ID_PREFIX}-tab-${key}`}
              aria-selected={active}
              aria-controls={`${STRUCTURE_TAB_ID_PREFIX}-panel-${key}`}
              sx={tabSx(active)}
              onClick={() => onTabChange(key)}
            >
              {label}
              {invalid && (
                <Tooltip title="This tab has a field that needs attention" placement="top" arrow>
                  <ErrorOutlineIcon
                    aria-label={`${label} has a field that needs attention`}
                    sx={{ fontSize: 15, color: 'error.main' }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
